<template>
  <div class="field">
    <label class="field__label" :for="name" :class="{ active: visible }">{{ label }}</label>
    <Multiselect
      v-model="innerValue"
      :options="options"
      :placeholder="label"
    >
    </Multiselect>
    <template v-if="validator?.$errors">
      <p v-for="(error, i) in validator?.$errors" :key="i" class="field__error">
        {{ error.$message }}
      </p>
    </template>
  </div>
</template>

<script lang="ts" setup>
import Multiselect from '@vueform/multiselect';
import { BaseValidation } from '@vuelidate/core';
import "@vueform/multiselect/themes/default.css";

const props = defineProps({
  modelValue: {
    type: [Array, Object, Boolean] as PropType<any | any[]>,
    default: ''
  },
  name: {
    type: String as PropType<string>,
    default: ''
  },
  label: {
    type: String as PropType<string>,
    default: ''
  },
  validator: {
    type: Object as PropType<BaseValidation>
  },
  options: {
    type: [Array, Object] as PropType<any[] | object>,
    default: []
  }
});

const emit = defineEmits(['update:modelValue']);

const innerValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const focused = ref<boolean>(false);
const visible = computed<boolean>(() => focused.value || (innerValue.value !== null && innerValue.value !== undefined && innerValue.value !== ''));
</script>

<style lang="scss">
.multiselect {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border-radius: 1rem;

}

// .multiselect-wrapper {
//   box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
//   border-radius: 1rem;
// }

.multiselect-placeholder {
  font-weight: 600;
  color: #{rgba($text, 0.73)};
}

.multiselect-dropdown {
  margin-top: 1rem;
  border: none;
  overflow: auto;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border: 2px solid $primary;

  ul.multiselect-options {
    padding-left: 0;

    li {
      margin: 0;
      line-height: 1.2;
      padding: 0.75rem;
      transition: all 135ms ease-in-out;
    }
  }
}


:root {
  --ms-font-size: 1rem;
  --ms-line-height: 1;
  --ms-bg: #FFFFFF;
  --ms-bg-disabled: #F3F4F6;
  --ms-border-color: #ffffff;
  --ms-border-width: 2px;
  --ms-border-color-active: #{$primary};
  --ms-border-width-active: 2px;
  --ms-radius: 1rem;
  --ms-py: 1rem;
  --ms-px: 1rem;
  --ms-ring-width: 0;
  --ms-ring-color: #{$primary};
  --ms-placeholder-color: #{rgba($text, 0.73)};
  --ms-max-height: 10rem;

  --ms-spinner-color: $primary;
  --ms-caret-color: #999999;
  --ms-clear-color: #999999;
  --ms-clear-color-hover: #000000;

  --ms-tag-remove-radius: 4px;
  --ms-tag-remove-py: 0.25rem;
  --ms-tag-remove-px: 0.25rem;
  --ms-tag-remove-my: 0rem;
  --ms-tag-remove-mx: 0.125rem;

  --ms-dropdown-bg: #FFFFFF;
  --ms-dropdown-border-color: #{$primary};
  --ms-dropdown-border-width: 2px;
  --ms-dropdown-radius: 1rem;

  --ms-group-label-py: 0.3rem;
  --ms-group-label-px: 0.75rem;
  --ms-group-label-line-height: 1.375;
  --ms-group-label-bg: #E5E7EB;
  --ms-group-label-color: #374151;
  --ms-group-label-bg-pointed: #D1D5DB;
  --ms-group-label-color-pointed: #374151;
  --ms-group-label-bg-disabled: #F3F4F6;
  --ms-group-label-color-disabled: #D1D5DB;
  --ms-group-label-bg-selected: $accent;
  --ms-group-label-color-selected: #FFFFFF;
  --ms-group-label-bg-selected-pointed: #0c9e70;
  --ms-group-label-color-selected-pointed: #FFFFFF;
  --ms-group-label-bg-selected-disabled: #75cfb1;
  --ms-group-label-color-selected-disabled: #D1FAE5;

  --ms-option-font-size: 1rem;
  --ms-option-line-height: 1.2;
  --ms-option-bg-pointed: #{$secondary};
  --ms-option-color-pointed: #FFFFFF;
  --ms-option-bg-selected: #{$primary};
  --ms-option-color-selected: #FFFFFF;
  --ms-option-bg-disabled: #FFFFFF;
  --ms-option-color-disabled: #D1D5DB;
  --ms-option-bg-selected-pointed: #{$primary_highlight};
  --ms-option-color-selected-pointed: #FFFFFF;
  --ms-option-bg-selected-disabled: #FFFFFF;
  --ms-option-color-selected-disabled: #D1FAE5;
  --ms-option-py: 0.5rem;
  --ms-option-px: 0.75rem;

  --ms-empty-color: #4B5563;
}
</style>
